import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { Shield, Plus, Edit, Trash2, ArrowLeft, XCircle, CheckCircle } from 'lucide-react';

const PermissionManagement = ({ onBack }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      // Assuming apiService has getAdminPermissions
      const data = await apiService.getAdminPermissions();
      setPermissions(data);
    } catch (error) {
      console.error("Failed to fetch permissions", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermission = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      if (editingPermission?.id) {
        await apiService.updateAdminPermission(editingPermission.id, data);
      } else {
        await apiService.createAdminPermission(data);
      }
      setEditingPermission(null);
      fetchPermissions();
    } catch (error) {
      console.error("Failed to save permission", error);
    }
  };

  const handleDeletePermission = async (id) => {
    if (!window.confirm('确定要删除吗？')) return;
    try {
      await apiService.deleteAdminPermission(id);
      fetchPermissions();
    } catch (error) {
      console.error("Failed to delete permission", error);
    }
  };

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-md overflow-hidden min-h-[500px]">
      <div className="p-4 border-b flex items-center justify-between bg-gray-50">
        <div className="flex items-center">
          <button 
            onClick={onBack} 
            className="mr-4 text-gray-600 hover:text-indigo-600 flex items-center transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            仪表盘
          </button>
          <h2 className="text-xl font-semibold text-gray-800">权限管理</h2>
        </div>
        <button
          onClick={() => setEditingPermission({})}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm flex items-center shadow-sm transition-colors"
        >
          <Plus className="h-4 w-4 mr-1" />
          新增权限
        </button>
      </div>

      <div className="p-6">
        {loading && <div className="text-center py-10 text-gray-500">加载中...</div>}

        {!loading && (
          <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">名称</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">代码</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">分类</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">描述</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {permissions.map((p, index) => (
                  <tr key={p.id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{p.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-mono">{p.code}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{p.category}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{p.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        onClick={() => setEditingPermission(p)} 
                        className="text-indigo-600 hover:text-indigo-900 mr-4 inline-flex items-center transition-colors"
                      >
                        <Edit size={14} className="mr-1" /> 编辑
                      </button>
                      <button 
                        onClick={() => handleDeletePermission(p.id)} 
                        className="text-red-600 hover:text-red-900 inline-flex items-center transition-colors"
                      >
                        <Trash2 size={14} className="mr-1" /> 删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Permission Edit Modal */}
        {editingPermission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">
                  {editingPermission.id ? '编辑权限' : '新增权限'}
                </h3>
                <button onClick={() => setEditingPermission(null)} className="text-gray-400 hover:text-gray-600">
                  <XCircle size={24} />
                </button>
              </div>
              
              <form onSubmit={handleSavePermission} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">权限名称</label>
                    <input 
                      name="name" 
                      defaultValue={editingPermission.name} 
                      required 
                      className="block w-full border border-gray-300 rounded-lg shadow-sm p-2.5 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" 
                      placeholder="例如：生成八字报告"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">权限代码 (Code)</label>
                    <input 
                      name="code" 
                      defaultValue={editingPermission.code} 
                      required 
                      className="block w-full border border-gray-300 rounded-lg shadow-sm p-2.5 focus:ring-indigo-500 focus:border-indigo-500 transition-colors font-mono text-sm" 
                      placeholder="例如：bazi:report:generate"
                    />
                    <p className="mt-1 text-xs text-gray-500">唯一标识符，代码中检查使用</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">分类</label>
                    <input 
                      name="category" 
                      defaultValue={editingPermission.category} 
                      className="block w-full border border-gray-300 rounded-lg shadow-sm p-2.5 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" 
                      placeholder="例如：bazi"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
                    <textarea 
                      name="description" 
                      defaultValue={editingPermission.description} 
                      rows={3}
                      className="block w-full border border-gray-300 rounded-lg shadow-sm p-2.5 focus:ring-indigo-500 focus:border-indigo-500 transition-colors" 
                      placeholder="权限描述..."
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button 
                    type="button" 
                    onClick={() => setEditingPermission(null)} 
                    className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                  >
                    取消
                  </button>
                  <button 
                    type="submit" 
                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 font-medium shadow-sm transition-colors"
                  >
                    保存
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionManagement;